/**
 * Country -> timezone mapping and UTC-to-local formatting for defect timestamps.
 *
 * Andes stores every defect timestamp as ISO 8601 in UTC with an explicit `Z`
 * (verified against energize_prod.andes_delivery_opportunities_prod_*,
 * andes_reservation_opportunities_prod_* and andes_defects_prod_*), so these
 * helpers only ever convert *from* UTC.
 *
 * Modelled on the Provider Dashboard (HarmonyFlexOpportunity), which formats
 * opportunity timestamps as `moment.tz(iso, timezone).format("YYYY-MM-DD HH:mm:ss z")`.
 * Two things are borrowed deliberately:
 *
 *   1. The rendered zone abbreviation (moment's trailing `z`). The label is always
 *      derived from the zone actually used, so a displayed time can never claim to
 *      be in a zone it was not converted to.
 *   2. Falling back to UTC rather than guessing (their DateConstants.DEFAULT_TIME_ZONE
 *      is "UTC"). An unknown country shows UTC, plainly labelled.
 *
 * Intl is used instead of their moment-timezone because this package has `moment`
 * but not `moment-timezone`, Intl already backs the timestamps on the standing panel,
 * and it needs no extra tz database in the bundle. Intl resolves DST per instant, so
 * a defect in July and one in December format with the correct offset and abbreviation.
 */

export const UTC = 'UTC';

/**
 * IANA zone per country in scope for ODQ/Reliability enforcement.
 *
 * Scope is exactly the countries that have ODQ/Reliability workflow templates, grouped by
 * the region owning their EWM Workflow table: us-east-1 (US, CA, MX, BR), eu-west-1
 * (GB, IN), us-west-2 (SG, AU, JP). Anything outside that set falls back to UTC rather
 * than being mapped speculatively.
 *
 * UK is carried alongside GB because both spellings reach this component depending on the
 * caller, exactly as in COUNTRY_TO_SCC_REGION.
 */
const COUNTRY_TIMEZONES: Record<string, string> = {
    // us-east-1
    US: 'America/Los_Angeles', CA: 'America/Toronto', MX: 'America/Mexico_City',
    BR: 'America/Sao_Paulo',
    // eu-west-1
    GB: 'Europe/London', UK: 'Europe/London', IN: 'Asia/Kolkata',
    // us-west-2
    SG: 'Asia/Singapore', AU: 'Australia/Sydney', JP: 'Asia/Tokyo',
};

/**
 * In-scope countries that span more than one timezone, where the single zone above is the
 * country's most populous/commercial zone and will be wrong for a DP working elsewhere in
 * that country.
 *
 * The defect row already carries a stationCode, which is the only signal that can
 * disambiguate these. There is no station -> timezone source available to the UI today, so
 * the country default stands and callers can use this set to mark the value as approximate
 * rather than presenting it as exact.
 */
const MULTI_TIMEZONE_COUNTRIES: ReadonlySet<string> = new Set([
    'US', 'CA', 'MX', 'BR', 'AU',
]);

/** The zone to display a country's timestamps in; UTC when the country is unknown. */
export const timezoneForCountry = (country?: string): string =>
    (country && COUNTRY_TIMEZONES[country.trim().toUpperCase()]) || UTC;

/** True when the country's zone is a country-level approximation. See MULTI_TIMEZONE_COUNTRIES. */
export const isApproximateTimezone = (country?: string): boolean =>
    !!country && MULTI_TIMEZONE_COUNTRIES.has(country.trim().toUpperCase());

/**
 * Standard/daylight abbreviation per zone.
 *
 * Intl cannot supply these consistently: with `timeZoneName: 'short'` each locale only
 * knows its own region's abbreviations and renders the rest as a `GMT±X` offset. For the
 * same instant, `en-US` gives PDT but turns IST into "GMT+5:30"; `en-IN` gives IST but
 * turns Pacific into "GMT-7"; `en-GB` gives BST and CEST but not IST. No single locale
 * covers the countries we operate in, so the abbreviations are held here -- the same thing
 * moment-timezone does internally, which is why the Provider Dashboard's `z` works.
 *
 * Only the strings live here. Whether standard or daylight applies is derived per instant
 * (see standardOffsetMinutes), so no entry encodes DST rules and a government changing them
 * cannot make this table silently wrong -- it would only affect the two labels.
 */
const ZONE_ABBREVIATIONS: Record<string, {std: string; dst: string}> = {
    'America/Los_Angeles': {std: 'PST', dst: 'PDT'},
    'America/Toronto': {std: 'EST', dst: 'EDT'},
    // Mexico dropped DST in 2022 and Brazil in 2019, so both labels are the same.
    'America/Mexico_City': {std: 'CST', dst: 'CST'},
    'America/Sao_Paulo': {std: 'BRT', dst: 'BRT'},
    'Europe/London': {std: 'GMT', dst: 'BST'},
    'Asia/Kolkata': {std: 'IST', dst: 'IST'},
    'Asia/Singapore': {std: 'SGT', dst: 'SGT'},
    'Asia/Tokyo': {std: 'JST', dst: 'JST'},
    'Australia/Sydney': {std: 'AEST', dst: 'AEDT'},
    [UTC]: {std: 'UTC', dst: 'UTC'},
};

// Constructing an Intl.DateTimeFormat is not cheap and the defects table formats several
// timestamps per row, so formatters are built once per zone.
const dateTimeFormatters = new Map<string, Intl.DateTimeFormat>();
const timeFormatters = new Map<string, Intl.DateTimeFormat>();
const dateKeyFormatters = new Map<string, Intl.DateTimeFormat>();

const formatterFor = (
    cache: Map<string, Intl.DateTimeFormat>,
    timeZone: string,
    options: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormat => {
    let formatter = cache.get(timeZone);
    if (!formatter) {
        formatter = new Intl.DateTimeFormat('en-US', {timeZone, ...options});
        cache.set(timeZone, formatter);
    }
    return formatter;
};

const parse = (iso: string | undefined): Date | undefined => {
    if (!iso) return undefined;
    const date = new Date(iso);
    return isNaN(date.getTime()) ? undefined : date;
};

const partsOf = (formatter: Intl.DateTimeFormat, date: Date): Record<string, string> => {
    const parts: Record<string, string> = {};
    formatter.formatToParts(date).forEach(part => { parts[part.type] = part.value; });
    return parts;
};

const offsetFormatters = new Map<string, Intl.DateTimeFormat>();

/** The zone's offset from UTC at that instant, in minutes (positive = ahead of UTC). */
const offsetMinutesAt = (date: Date, timeZone: string): number => {
    const parts = partsOf(formatterFor(offsetFormatters, timeZone, {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
    }), date);
    // Reading the wall-clock fields back as if they were UTC gives the shifted instant; the
    // difference from the real instant is the offset. hourCycle can render midnight as 24.
    const hour = parts.hour === '24' ? '00' : parts.hour;
    const asUtc = Date.UTC(
        Number(parts.year), Number(parts.month) - 1, Number(parts.day),
        Number(hour), Number(parts.minute), Number(parts.second),
    );
    return Math.round((asUtc - date.getTime()) / 60000);
};

/**
 * The zone's non-daylight offset, taken as the smaller of its January and July offsets.
 *
 * That holds in both hemispheres: London is 0 in January and +60 in July, Sydney is +660 in
 * January and +600 in July, and in each case the standard offset is the minimum. A zone with
 * no DST has both equal, so it is trivially standard all year.
 */
const standardOffsetMinutes = (date: Date, timeZone: string): number => {
    const year = date.getUTCFullYear();
    return Math.min(
        offsetMinutesAt(new Date(Date.UTC(year, 0, 15)), timeZone),
        offsetMinutesAt(new Date(Date.UTC(year, 6, 15)), timeZone),
    );
};

/**
 * The zone's abbreviation at that instant, e.g. `PDT` in July and `PST` in December.
 * Falls back to whatever Intl offers for a zone absent from ZONE_ABBREVIATIONS.
 */
const abbreviationAt = (date: Date, timeZone: string): string => {
    const entry = ZONE_ABBREVIATIONS[timeZone];
    if (entry) {
        const isDaylight = offsetMinutesAt(date, timeZone) > standardOffsetMinutes(date, timeZone);
        return isDaylight ? entry.dst : entry.std;
    }
    return partsOf(formatterFor(dateTimeFormatters, timeZone, {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: '2-digit', hour12: true, timeZoneName: 'short',
    }), date).timeZoneName ?? '';
};

/**
 * `12:00 AM PDT, Sep 7, 2026` -- time first, matching the order auditors already read
 * on this table, with the real zone abbreviation in place of the previously hardcoded
 * "IST". Returns the em dash for a missing or unparseable value.
 */
export const formatInTimezone = (iso: string | undefined, timeZone: string): string => {
    const date = parse(iso);
    if (!date) return '—';
    const parts = partsOf(formatterFor(dateTimeFormatters, timeZone, {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: '2-digit', hour12: true, timeZoneName: 'short',
    }), date);
    return `${parts.hour}:${parts.minute} ${parts.dayPeriod} ${abbreviationAt(date, timeZone)}, `
        + `${parts.month} ${parts.day}, ${parts.year}`;
};

/** Clock time only (`12:00 AM`), for ranges that carry the zone label once alongside them. */
export const formatTimeOnlyInTimezone = (iso: string | undefined, timeZone: string): string => {
    const date = parse(iso);
    if (!date) return '—';
    const parts = partsOf(formatterFor(timeFormatters, timeZone, {
        hour: 'numeric', minute: '2-digit', hour12: true,
    }), date);
    return `${parts.hour}:${parts.minute} ${parts.dayPeriod}`;
};

/** `Sep 7, 2026` in the given zone -- the date the range above belongs to. */
export const formatDateInTimezone = (iso: string | undefined, timeZone: string): string => {
    const date = parse(iso);
    if (!date) return '';
    const parts = partsOf(formatterFor(dateTimeFormatters, timeZone, {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: '2-digit', hour12: true, timeZoneName: 'short',
    }), date);
    return `${parts.month} ${parts.day}, ${parts.year}`;
};

/** The zone's abbreviation at that instant (`PDT` vs `PST`), for labelling a range once. */
export const timezoneAbbreviation = (iso: string | undefined, timeZone: string): string => {
    const date = parse(iso);
    if (!date) return '';
    return abbreviationAt(date, timeZone);
};

/**
 * The calendar date at that instant in the given zone, as `YYYY-MM-DD`.
 *
 * Used as both the Defect Date column value and its filter key, so the two cannot
 * disagree. Taking `iso.substring(0, 10)` instead would yield the UTC date, which is a
 * day off from the DP's date for any defect falling near local midnight -- most visibly
 * in AU and JP, where a large part of the local day is the previous UTC day.
 *
 * Kept zero-padded and year-first so it still sorts and groups as plain text.
 */
export const localDateKey = (iso: string | undefined, timeZone: string): string => {
    const date = parse(iso);
    if (!date) return '';
    const parts = partsOf(formatterFor(dateKeyFormatters, timeZone, {
        year: 'numeric', month: '2-digit', day: '2-digit',
    }), date);
    return `${parts.year}-${parts.month}-${parts.day}`;
};

/**
 * The underlying UTC instant, kept available as hover text next to every converted
 * value. For a multi-timezone country the local time is an approximation, so the
 * auditor always needs the unambiguous value within reach.
 */
export const formatUtcTooltip = (iso: string | undefined): string => {
    const date = parse(iso);
    if (!date) return '';
    return `${date.toISOString().replace('.000Z', 'Z')} (UTC)`;
};
